import { Injectable } from '@angular/core';
import { Observable, combineLatest, distinctUntilChanged, map, shareReplay } from 'rxjs';
import { MqttConnectorService } from 'src/mqtt/services/mqtt-connector.service';
import { IShadowState } from 'src/mqtt/interfaces/interfaces';
import {
  DEFAULT_DURATION, MOOD_AMP_ENTITY, MOOD_DURATIONS, MOOD_LIBRARY_SENSOR,
  MOOD_STATE_SENSOR, MoodDuration, MoodSound, clampVolume, deriveName, resolveLibrary,
} from '../consts/mood-audio';

export type MoodAudioStatus = 'idle' | 'playing' | 'paused';

export interface MoodAudioState {
  status: MoodAudioStatus;
  /** The current selection, resolved to a display name. Null only when there is no library. */
  sound: MoodSound | null;
  duration: MoodDuration;
  /**
   * Mood audio's own remembered level, 1-100 — NOT the amplifier's live
   * `volume_level`, which while idle holds whatever Bluetooth last left it at.
   *
   * A scene that pins a volume writes this too, so starting a scene moves the
   * slider exactly as dragging it would, and the level stays put afterwards.
   */
  volume: number;
  library: MoodSound[];
  /** False when the amplifier is missing from the shadow or reporting unavailable. */
  ampPresent: boolean;
  /**
   * Whether the unit has actually told us anything yet.
   *
   * NOT the same as "the stream emitted". getMessageListener maps
   * storage.items$, a BehaviorSubject, so every listener fires synchronously on
   * subscribe with whatever the map holds — an empty one, on a cold start. A
   * consumer treating that first emission as news reads "no amplifier, no
   * sounds" and acts on it, which is how the transport bar came to render grey
   * on its very first frame.
   */
  hydrated: boolean;
}

/**
 * Everything the view actually reads. Names are derived from ids by the static
 * catalog, so comparing ids covers the display names too.
 */
function sameState(a: MoodAudioState, b: MoodAudioState): boolean {
  return a.status === b.status
    && a.sound?.id === b.sound?.id
    && a.duration === b.duration
    && a.volume === b.volume
    && a.ampPresent === b.ampPresent
    && a.hydrated === b.hydrated
    && sameLibrary(a.library, b.library);
}

function sameLibrary(a: MoodSound[], b: MoodSound[]): boolean {
  return a === b || (a.length === b.length && a.every((s, i) => s.id === b[i].id));
}

/**
 * Mood Audio state and commands.
 *
 * State comes from two published entities. `sensor.mood_audio` is a template
 * sensor on the unit that already collapses the engine's session flag and the
 * amplifier's playback state into one string — the client does not re-derive
 * that, because the amplifier is shared with Bluetooth, the alarm siren and
 * the holiday light-sync, and only the engine knows whether a session is ours.
 */
@Injectable({ providedIn: 'root' })
export class MoodAudioService {
  public readonly state$: Observable<MoodAudioState>;

  /**
   * Last `file_list` seen, and the library resolved from it. resolveLibrary is
   * pure, so an unchanged list can hand back the SAME array — and it has to:
   * consumers bind `library` straight into sound-picker's `sounds` input, and a
   * fresh array identity fires ngOnChanges, which used to reset the wheel under
   * a resident's finger. See the memo note on state$ below.
   */
  private libraryKey: string | null = null;
  private library: MoodSound[] = [];

  constructor(private mqtt: MqttConnectorService) {
    // No startWith(undefined): getMessageListener maps storage.items$, which is a
    // BehaviorSubject, so each listener already emits synchronously on subscribe.
    // The startWith calls only bought a duplicate first emission.
    this.state$ = combineLatest([
      this.mqtt.getMessageListener(MOOD_STATE_SENSOR),
      this.mqtt.getMessageListener(MOOD_LIBRARY_SENSOR),
      this.mqtt.getMessageListener(MOOD_AMP_ENTITY),
    ]).pipe(
      map(([mood, folder, amp]) => this.toState(mood, folder, amp)),
      // items$ re-emits the whole device-state map on EVERY message, so all three
      // listeners fire on traffic that has nothing to do with audio — worst while
      // playing, when the amplifier republishes every few seconds. Drop the no-op
      // emissions here rather than making every consumer defend against them.
      distinctUntilChanged(sameState),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /**
   * resolveLibrary, memoised on the reported file_list. Suppressing no-op
   * emissions is not enough on its own: a GENUINE change (a pause, say) must not
   * carry a brand-new library array with it, or the picker's wheel resets anyway.
   */
  private resolveLibraryMemo(fileList: string[] | undefined): MoodSound[] {
    const key = JSON.stringify(fileList ?? null);
    if (key !== this.libraryKey) {
      this.libraryKey = key;
      this.library = resolveLibrary(fileList);
    }
    return this.library;
  }

  private toState(
    mood: IShadowState | undefined,
    folder: IShadowState | undefined,
    amp: IShadowState | undefined,
  ): MoodAudioState {
    const library = this.resolveLibraryMemo(
      folder?.reported?.attributes?.['file_list'] as string[] | undefined,
    );

    const reported = mood?.reported?.state;
    const status: MoodAudioStatus =
      reported === 'playing' || reported === 'paused' ? reported : 'idle';

    const stem = String(mood?.reported?.attributes?.['sound'] ?? '').trim();
    // A stem the unit no longer has (or a scene-started sound) still gets a name,
    // so the bar never shows a blank while audio is audible.
    const sound: MoodSound | null = stem
      ? library.find((s) => s.id === stem) ?? { id: stem, name: deriveName(stem) }
      : library[0] ?? null;

    const rawDuration = Number(mood?.reported?.attributes?.['duration']);
    const duration: MoodDuration = MOOD_DURATIONS.includes(rawDuration as MoodDuration)
      ? (rawDuration as MoodDuration)
      : DEFAULT_DURATION;

    // Published as an attribute on the state sensor rather than read from
    // input_number.mood_audio_volume directly: input_number.* is filtered out of
    // the AWS-IoT shadow by iot-core's allowlist, so the helper never reaches us.
    //
    // ONE level, not two. A scene that pins a volume writes this same helper, so
    // its level is adopted as the resident's own and survives the session ending
    // — there is no separate "what is playing right now" value to reconcile.
    const volume = clampVolume(Number(mood?.reported?.attributes?.['volume']));

    const ampState = amp?.reported?.state;
    const ampPresent = !!amp?.reported?.entity_id && ampState !== 'unavailable';

    // Either entity appearing in the shadow means the map has been populated —
    // the amplifier reporting `unavailable` still counts, since that is the unit
    // telling us something rather than us not having looked yet.
    const hydrated = !!mood?.reported?.entity_id || !!amp?.reported?.entity_id;

    return { status, sound, duration, volume, library, ampPresent, hydrated };
  }

  public play(soundId: string, duration: MoodDuration): Observable<void> {
    return this.mqtt.callHaAction('script', 'mood_audio_start', { sound: soundId, duration });
  }

  public stop(): Observable<void> {
    return this.mqtt.callHaAction('script', 'mood_audio_stop');
  }

  public select(soundId: string, duration: MoodDuration): Observable<void> {
    return this.mqtt.callHaAction('script', 'mood_audio_select', { sound: soundId, duration });
  }

  /**
   * Set the mood-audio level. The unit stores it and, only if a session is
   * actually live, applies it to the amplifier — so adjusting while nothing is
   * playing stages the level instead of moving Bluetooth's volume.
   *
   * Clamped here as well as on the unit: input_number.set_value raises on a value
   * outside its range, which would abort the script half-applied.
   */
  public setVolume(volume: number): Observable<void> {
    return this.mqtt.callHaAction('script', 'mood_audio_volume_set', {
      volume: clampVolume(volume),
    });
  }

}
